import { useMemo } from 'react'
import Select from 'react-select'
import { customStyles } from './styles'
import { BACKEND_SUPPORTED_PAGINATION_LIMIT } from 'constants/api'
import { PAGINATION_LIMIT } from 'constants/api'
import classNames from 'classnames'
import './index.scss'
import ListPaginationWithEllipsis from 'common/ListPaginationWithEllipsis'

/**
 * This component has logic referenced from:
 * https://www.freecodecamp.org/news/build-a-custom-pagination-component-in-react/
 */

interface ListPaginationProps extends PaginationSchema {
  onNextPage: (offset: number) => void
  onPreviousPage: (offset: number) => void
  onPageChange: (offset: number) => void
  onLimitChange: (option: SelectNumberItem) => void
  limitPostfixText: string
}

const DropdownIndicator = (props) => {
  const { menuIsOpen } = props.selectProps

  return (
    <img
      src="/icons/pagination-arrow-up.svg"
      srcSet="/icons/pagination-arrow-up.webp"
      height={11}
      width={18}
      className={classNames('PaginationContainer__dropdownIndicatorIcon', {
        'PaginationContainer__dropdownIndicatorIcon--downArrow': menuIsOpen,
      })}
      alt="Pagination up arrow"
    />
  )
}

const ListPagination: React.FC<ListPaginationProps> = ({
  limit,
  count,
  offset,
  onNextPage,
  onPreviousPage,
  onPageChange,
  onLimitChange,
  limitPostfixText,
}) => {
  const onPreviousPageClick = () => {
    onPreviousPage(offset - limit >= 0 ? offset - limit : 0)
  }

  const onNextPageClick = () => {
    onNextPage(offset + limit < count ? offset + limit : offset)
  }

  const onPageNumberClick = (page: number) => {
    onPageChange((Number(page) - 1) * limit)
  }

  const totalPages = Math.ceil(count / limit)
  const currentPage = offset / limit + 1 // page we are on. + 1 to start currentPage from 1
  const pageLimit: SelectNumberItem = useMemo(
    () => ({ label: limit.toString(), value: limit }),
    [limit]
  )
  const options: SelectNumberItem[] = useMemo(() => {
    if (count) {
      const options: SelectNumberItem[] = []

      // The second condition in the loop which is "|| (i - count > 0 && i - count < 10);"
      // Would include the last page in the pagination count, so if the count is 16 the pagination limit would show 10 and 20 in the limit
      for (
        let i = PAGINATION_LIMIT;
        i < count || (i - count >= 0 && i - count < PAGINATION_LIMIT);
        i += PAGINATION_LIMIT
      ) {
        // show limit options till the backend supported number
        // which is 500 currently
        if (i > BACKEND_SUPPORTED_PAGINATION_LIMIT) {
          break
        }

        options.push({ label: i.toString(), value: i })
      }

      return options
    }

    return [{ label: '10', value: 10 }]
  }, [count])
  return (
    <div className="w-100 d-flex justify-content-md-between justify-content-center">
      <ListPaginationWithEllipsis
        totalPages={totalPages}
        currentPage={currentPage}
        changePage={onPageNumberClick}
        onNextPage={onNextPageClick}
        onPreviousPage={onPreviousPageClick}
      />
      <div className="PaginationLimitContainer">
        <div className="mr-3">Show</div>
        <Select
          aria-label="page limit"
          onChange={onLimitChange}
          styles={customStyles}
          options={options}
          isSearchable={false}
          components={{ DropdownIndicator }}
          defaultValue={[{ label: '10', value: 10 }]}
          placeholder={pageLimit}
          value={pageLimit}
        />
        <div className="ml-3">{limitPostfixText}</div>
      </div>
    </div>
  )
}

export default ListPagination
